import React, { useState } from 'react';
import { authAPI } from '../services/api';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await authAPI.login(email, password);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      window.location.href = '/dashboard';
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .login-root {
          min-height: 100vh;
          display: flex;
          font-family: 'Sora', sans-serif;
          background-color: #050d1a;
          position: relative;
          overflow: hidden;
        }

        /* Animated background grid */
        .login-root::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(0, 87, 154, 0.12) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 87, 154, 0.12) 1px, transparent 1px);
          background-size: 48px 48px;
          animation: gridShift 20s linear infinite;
        }

        @keyframes gridShift {
          0% { background-position: 0 0; }
          100% { background-position: 48px 48px; }
        }

        /* Glow orb */
        .glow-orb {
          position: absolute;
          width: 600px;
          height: 600px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(0, 120, 210, 0.18) 0%, transparent 70%);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          pointer-events: none;
          animation: pulse 6s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.8; }
          50% { transform: translate(-50%, -50%) scale(1.15); opacity: 1; }
        }

        /* Left panel */
        .login-left {
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 80px;
          position: relative;
          z-index: 1;
        }

        @media (max-width: 900px) {
          .login-left { display: none; }
        }

        .brand-mark {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 64px;
        }

        .brand-icon {
          width: 44px;
          height: 44px;
          background: linear-gradient(135deg, #0078d2, #00a8ff);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 700;
          color: #fff;
          font-family: 'JetBrains Mono', monospace;
          letter-spacing: -1px;
        }

        .brand-name {
          font-size: 18px;
          font-weight: 600;
          color: #ffffff;
          letter-spacing: 0.5px;
        }

        .hero-text h1 {
          font-size: clamp(36px, 4vw, 52px);
          font-weight: 700;
          color: #ffffff;
          line-height: 1.15;
          margin-bottom: 20px;
          letter-spacing: -1px;
        }

        .hero-text h1 span {
          background: linear-gradient(135deg, #0094ff, #00d4ff);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-text p {
          font-size: 16px;
          color: rgba(255,255,255,0.45);
          line-height: 1.7;
          max-width: 380px;
          font-weight: 300;
        }

        .stat-row {
          display: flex;
          gap: 40px;
          margin-top: 56px;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .stat-number {
          font-size: 28px;
          font-weight: 700;
          color: #ffffff;
          font-family: 'JetBrains Mono', monospace;
        }

        .stat-label {
          font-size: 12px;
          color: rgba(255,255,255,0.35);
          text-transform: uppercase;
          letter-spacing: 1.5px;
          font-weight: 500;
        }

        /* Right panel / form */
        .login-right {
          width: 480px;
          min-height: 100vh;
          background: rgba(255,255,255, 0.03);
          border-left: 1px solid rgba(255,255,255,0.06);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 48px 40px;
          position: relative;
          z-index: 1;
          backdrop-filter: blur(20px);
        }

        @media (max-width: 900px) {
          .login-right {
            width: 100%;
            border-left: none;
          }
        }

        .form-card {
          width: 100%;
          max-width: 380px;
        }

        .form-header {
          margin-bottom: 40px;
        }

        .form-header h2 {
          font-size: 26px;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 8px;
          letter-spacing: -0.5px;
        }

        .form-header p {
          font-size: 14px;
          color: rgba(255,255,255,0.35);
          font-weight: 300;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: rgba(255,255,255,0.5);
          text-transform: uppercase;
          letter-spacing: 1.2px;
          margin-bottom: 8px;
        }

        .input-wrapper {
          position: relative;
        }

        .input-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(255,255,255,0.25);
          font-size: 15px;
          pointer-events: none;
          transition: color 0.2s;
        }

        .form-input {
          width: 100%;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          padding: 14px 16px 14px 44px;
          font-size: 15px;
          color: #ffffff;
          font-family: 'Sora', sans-serif;
          outline: none;
          transition: all 0.2s ease;
        }

        .form-input::placeholder {
          color: rgba(255,255,255,0.2);
        }

        .form-input:focus {
          background: rgba(0, 148, 255, 0.08);
          border-color: rgba(0, 148, 255, 0.5);
          box-shadow: 0 0 0 3px rgba(0, 148, 255, 0.1);
        }

        .form-input:focus + .input-icon,
        .input-wrapper:focus-within .input-icon {
          color: rgba(0, 148, 255, 0.7);
        }

        .error-box {
          display: flex;
          align-items: center;
          gap: 10px;
          background: rgba(255, 77, 79, 0.1);
          border: 1px solid rgba(255, 77, 79, 0.25);
          border-radius: 10px;
          padding: 12px 16px;
          margin-bottom: 20px;
          color: #ff7875;
          font-size: 13px;
          font-weight: 400;
          animation: shake 0.4s ease;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          75% { transform: translateX(6px); }
        }

        .submit-btn {
          width: 100%;
          padding: 15px;
          background: linear-gradient(135deg, #0078d2, #0094ff);
          border: none;
          border-radius: 10px;
          color: #ffffff;
          font-size: 15px;
          font-weight: 600;
          font-family: 'Sora', sans-serif;
          cursor: pointer;
          transition: all 0.25s ease;
          margin-top: 8px;
          position: relative;
          overflow: hidden;
          letter-spacing: 0.3px;
        }

        .submit-btn::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.1), transparent);
          opacity: 0;
          transition: opacity 0.2s;
        }

        .submit-btn:hover:not(:disabled)::after { opacity: 1; }
        .submit-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 24px rgba(0, 120, 210, 0.4);
        }
        .submit-btn:active:not(:disabled) { transform: translateY(0); }
        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
          vertical-align: middle;
          margin-right: 8px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .divider {
          height: 1px;
          background: rgba(255,255,255,0.07);
          margin: 28px 0;
        }

        .footer-note {
          text-align: center;
          font-size: 12px;
          color: rgba(255,255,255,0.2);
          font-weight: 300;
        }

        .footer-note strong {
          color: rgba(255,255,255,0.35);
          font-weight: 500;
        }
      `}</style>

      <div className="login-root">
        <div className="glow-orb" />

        {/* Left hero panel */}
        <div className="login-left">
          <div className="brand-mark">
            <div className="brand-icon">Loop</div>
            <span className="brand-name">CMS Platform</span>
          </div>

          <div className="hero-text">
            <h1>Manage your<br /><span>content</span><br />with confidence.</h1>
            <p>A unified workspace for bids, blogs, vacancies, projects, and everything your organisation publishes.</p>
          </div>

          <div className="stat-row">
            <div className="stat-item">
              <span className="stat-number">∞</span>
              <span className="stat-label">Modules</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">∞</span>
              <span className="stat-label">Content</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">1</span>
              <span className="stat-label">Centralized Dashboard</span>
            </div>
          </div>
        </div>

        {/* Right form panel */}
        <div className="login-right">
          <div className="form-card">
            <div className="form-header">
              <h2>Welcome back</h2>
              <p>Sign in to your CMS account to continue</p>
            </div>

            {error && (
              <div className="error-box">
                <span>⚠</span>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label" htmlFor="email">Email address</label>
                <div className="input-wrapper">
                  <input
                    id="email"
                    type="email"
                    className="form-input"
                    placeholder="you@organisation.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <span className="input-icon" style={{ pointerEvents: 'none' }}>✉</span>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="password">Password</label>
                <div className="input-wrapper">
                  <input
                    id="password"
                    type="password"
                    className="form-input"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <span className="input-icon" style={{ pointerEvents: 'none' }}>🔒</span>
                </div>
              </div>

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? (
                  <><span className="spinner" />Signing in...</>
                ) : (
                  'Sign in →'
                )}
              </button>
            </form>

            <div className="divider" />
            <p className="footer-note">
              Access restricted to <strong>authorised personnel</strong> only.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;